import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Input,
  Row,
  Statistic,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  theme
} from "antd";
import {
  FileTextOutlined,
  LoadingOutlined,
  PlusOutlined,
  WarningOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../../commons/GeneralErrorAlert";
import { hasPermission } from "../../../../commons/Permissions/PermissionsHook";
import { UserContext } from "../../../../context/UserContext";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import Amount from "../shared/Amount";
import PageHeader from "../shared/PageHeader";
import { formatCents, formatDate } from "../shared/money";
import BelegDrawer from "./BelegDrawer";
import BelegUpload from "./BelegUpload";
import SelfBillingModal from "./SelfBillingModal";
import { getMeta, listBelege } from "./api";
import { POLL_MS, STATUS_COLORS, STATUS_LABELS } from "./constants";

const MODULE = "Accounting";
const TABS = [
  { key: "inbox", label: "Inbox" },
  { key: "approved", label: "Approved" },
  { key: "booked", label: "Booked" },
  { key: "rejected", label: "Rejected" },
  { key: "self_billing", label: "Self-billing" },
  { key: "all", label: "All" }
];

/** The receipts inbox: upload, list by status, open a Beleg for review. */
const BelegeInbox = () => {
  const { t } = useTranslation("accounting");
  const user = useContext(UserContext);
  const can = action => hasPermission(user?.permissions, MODULE, action);
  const isMobile = useIsMobile();
  const { token } = theme.useToken();
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("inbox");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({
    belege: [],
    total: 0,
    totals: { countByStatus: {}, openGross: 0 }
  });
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, id: null });
  const [selfBillingOpen, setSelfBillingOpen] = useState(false);
  const pollTimer = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listBelege(
        tab === "self_billing"
          ? { status: "all", kind: "self_billing", q, page, limit: 25 }
          : { status: tab, q, page, limit: 25 }
      );
      const belege = Array.isArray(res.data?.belege) ? res.data.belege : [];
      setData({ ...res.data, belege });
    } catch (err) {
      if (!silent) generalErrorAlert(err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
  }, []);

  useEffect(() => {
    load();
  }, [tab, q, page]);

  // While any document is still being read, refresh quietly.
  useEffect(() => {
    clearTimeout(pollTimer.current);
    if (data.belege.some(b => b.extraction?.status === "processing")) {
      pollTimer.current = setTimeout(() => load(true), POLL_MS);
    }
    return () => clearTimeout(pollTimer.current);
  }, [data]);

  const counts = data.totals?.countByStatus || {};

  const columns = [
    {
      title: t("Date"),
      dataIndex: "invoiceDate",
      width: 110,
      render: (value, row) =>
        value ? (
          formatDate(value)
        ) : (
          <Typography.Text type="secondary">{formatDate(row.createdAt)}</Typography.Text>
        )
    },
    {
      title: t("Supplier"),
      dataIndex: "supplierName",
      ellipsis: true,
      mobile: "title",
      render: (value, row) => (
        <span className="acct-wrap">
          {row.extraction?.status === "processing" && (
            <LoadingOutlined style={{ marginInlineEnd: 6 }} />
          )}
          {value || row.supplier?.name || (
            <Typography.Text type="secondary">{row.originalName}</Typography.Text>
          )}
        </span>
      )
    },
    { title: t("Invoice number"), dataIndex: "invoiceNumber", width: 150, ellipsis: true },
    {
      title: t("Beleg no."),
      dataIndex: "belegNumber",
      width: 140,
      responsive: ["lg"],
      mobile: "hidden"
    },
    {
      title: t("Gross"),
      dataIndex: ["amounts", "gross"],
      width: 120,
      align: "end",
      mobile: "subtitle",
      render: (value, row) =>
        value ? (
          <span className="acct-wrap">
            <Amount cents={value} large={isMobile} />
            {row.creditedAmount > 0 && (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                {t("credited")} {formatCents(row.creditedAmount)}
              </Typography.Text>
            )}
            {row.kind === "credit_note" && row.remainingCredit > 0 && (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                {t("credit left")} {formatCents(row.remainingCredit)}
              </Typography.Text>
            )}
          </span>
        ) : (
          ""
        )
    },
    {
      title: t("Status"),
      dataIndex: "status",
      width: 130,
      render: (value, row) => (
        <Tag color={STATUS_COLORS[value]}>
          {row.extraction?.status === "processing"
            ? t("Reading…")
            : t(STATUS_LABELS[value] || value)}
        </Tag>
      )
    },
    {
      title: t("Confidence"),
      dataIndex: ["extraction", "confidence"],
      width: 100,
      responsive: ["md"],
      mobile: "hidden",
      render: (value, row) => {
        if (row.extraction?.status !== "done" || value === null || value === undefined) return "";
        const pct = Math.round(value * 100);
        return <Tag color={pct >= 80 ? "green" : "orange"}>{pct} %</Tag>;
      }
    },
    {
      title: "",
      key: "issues",
      width: 60,
      mobileLabel: t("Warnings"),
      render: (_, row) => {
        const issues = [
          ...(row.extraction?.warnings || []),
          ...(row.compliance?.complete === false ? [t("Missing § 14 UStG details")] : []),
          ...(row.duplicateOf ? [t("Possible duplicate")] : []),
          ...(row.extraction?.status === "failed" ? [row.extraction.error] : [])
        ];
        // Phone cards have no hover: show the warnings as text (the drawer lists them too).
        if (isMobile) {
          if (!issues.length && !row.einvoice?.format) return null;
          return (
            <span className="acct-wrap">
              {issues.length > 0 && (
                <Typography.Text type="warning" style={{ display: "block" }}>
                  <WarningOutlined style={{ marginInlineEnd: 6 }} />
                  {issues.join(" · ")}
                </Typography.Text>
              )}
              {row.einvoice?.format && (
                <Tag color="purple" icon={<FileTextOutlined />}>
                  {t("E-invoice")}
                </Tag>
              )}
            </span>
          );
        }
        return (
          <span>
            {issues.length > 0 && (
              <Tooltip title={issues.join(" · ")}>
                <WarningOutlined
                  style={{ color: token.colorWarning, marginInlineEnd: 6 }}
                  aria-label={issues.join(" · ")}
                />
              </Tooltip>
            )}
            {row.einvoice?.format && (
              <Tooltip title={t("E-invoice")}>
                <FileTextOutlined style={{ color: token.purple }} aria-label={t("E-invoice")} />
              </Tooltip>
            )}
          </span>
        );
      }
    }
  ];

  return (
    <div>
      <PageHeader
        title={t("Receipts inbox")}
        subtitle={
          meta && !meta.ai?.configured
            ? t("AI reading is not configured; e-invoices are still read.")
            : null
        }
        extra={
          can("Create") ? (
            <>
              <Button icon={<PlusOutlined />} onClick={() => setSelfBillingOpen(true)}>
                {isMobile ? t("Gutschrift") : t("Create Gutschrift (self-billing)")}
              </Button>
              <BelegUpload compact onUploaded={() => load(true)} />
            </>
          ) : null
        }
      />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Phones upload through the header buttons (file + camera); the drop zone is desktop-only. */}
        {can("Create") && !isMobile && (
          <Col xs={24} lg={16}>
            <Card size="small">
              <BelegUpload onUploaded={() => load(true)} />
            </Card>
          </Col>
        )}
        <Col xs={24} lg={8}>
          <Card size="small" className="acct-kpi">
            <Row gutter={[8, 8]}>
              <Col xs={12} sm={8}>
                <Statistic
                  title={t("To review")}
                  value={(counts.needs_review || 0) + (counts.extracted || 0) + (counts.new || 0)}
                />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title={t("Approved")} value={counts.approved || 0} />
              </Col>
              <Col xs={24} sm={8}>
                <Statistic
                  title={t("Open payables")}
                  value={formatCents(data.totals?.openGross || 0)}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      <PageToolbar
        filters={
          <Input.Search
            allowClear
            placeholder={t("Search supplier, number, text")}
            onSearch={value => {
              setQ(value);
              setPage(1);
            }}
          />
        }
      />
      <Card size="small">
        <Tabs
          activeKey={tab}
          onChange={key => {
            setTab(key);
            setPage(1);
          }}
          items={TABS.map(item => ({
            key: item.key,
            label: (
              <Badge
                count={["all", "self_billing"].includes(item.key) ? 0 : counts[item.key] || 0}
                size="small"
                offset={[8, -2]}
                overflowCount={999}
              >
                {t(item.label)}
              </Badge>
            )
          }))}
        />
        <ResponsiveTable
          rowKey="_id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={data.belege}
          onRow={row => ({
            onClick: () => setDrawer({ open: true, id: row._id }),
            style: { cursor: "pointer" }
          })}
          pagination={{
            current: page,
            pageSize: 25,
            total: data.total,
            onChange: setPage,
            showSizeChanger: false
          }}
        />
      </Card>
      <BelegDrawer
        open={drawer.open}
        belegId={drawer.id}
        meta={meta}
        canEdit={can("Update")}
        canBook={can("Create")}
        onClose={() => {
          setDrawer({ open: false, id: null });
          load(true);
        }}
        onChanged={() => load(true)}
      />
      <SelfBillingModal
        open={selfBillingOpen}
        onClose={() => setSelfBillingOpen(false)}
        onDone={() => {
          setSelfBillingOpen(false);
          setTab("self_billing");
          load(true);
        }}
      />
    </div>
  );
};

export default BelegeInbox;
